import { Card } from '@/components/common/card'
import { ErrorIcon } from '@/components/icons/error'
import type { FixedTermMarket } from '@/utils/internalAppSdk'

interface Props {
  market: FixedTermMarket
}

export const FixedTermMarketYieldAssumptions = ({ market }: Props) => {
  const accountingAssetName = market.accountingAsset.symbol ?? market.accountingAsset.name
  const redemptionValue = accountingAssetName ? `1 ${accountingAssetName}` : 'one unit of the accounting asset'
  const denomination = accountingAssetName ?? 'the accounting asset'

  return (
    <aside className="flex items-center gap-3 rounded-2xl border bg-surface p-4">
      <ErrorIcon size={16} className="shrink-0 text-warning" aria-hidden />
      <div className="flex flex-col gap-1">
        <h2 className="font-medium text-heading-foreground text-xs">Assumptions:</h2>
        <p className="text-body-foreground text-xs">
          Projected yield assumes each PT is held to maturity and redeemed for {redemptionValue}. Realized return
          depends on the PT price at entry and exit. Returns are denominated in {denomination} and depend on the
          underlying protocol and Pendle performing as intended, and are not guaranteed in USD terms.
        </p>
      </div>
    </aside>
  )
}

export const FixedTermMarketYieldExplanation = () => (
  <Card as="section" className="flex flex-col gap-2 p-4 md:p-5">
    <h2 className="font-medium text-base text-heading-foreground">Where does the yield come from?</h2>
    <p className="text-body-foreground text-sm">
      Pendle splits a yield-bearing asset into two separately tradable tokens: a Principal Token (PT) and a Yield Token
      (YT). PT yield comes from the discount at purchase: each PT is bought below the asset&apos;s full value and
      redeems for that full value on its maturity date. The difference is the return fixed at the time of purchase,
      provided the PT is held until maturity.
    </p>
  </Card>
)
